import { useEffect, useRef, useState } from 'react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useLocalStorage } from '@frontend/web-storage';
import { theme } from '@frontend/theme';
import { BannerNotification, BannerProps } from '@frontend/design-system';

type EphemeralRow = {
  enabled: boolean;
  id: string;
  name: string;
  value: string;
};

export const WEAVE_EPHEMERAL_STORAGE_KEY = 'weavemeral-active';

export type EphemeralState = {
  isVisible: boolean;
  height: number;
};
type EphemeralActiveBannerProps = Omit<Partial<BannerProps>, 'children' | 'message'> & {
  onChangeVisibility: (props: EphemeralState) => void;
};
export const EphemeralActiveBanner = ({
  className,
  status,
  title,
  onChangeVisibility,
  ...props
}: EphemeralActiveBannerProps) => {
  const { t } = useTranslation('dashboard');

  const [height, setHeight] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  const [rows] = useLocalStorage<EphemeralRow[]>({
    key: WEAVE_EPHEMERAL_STORAGE_KEY,
    defaultValue: [],
  });

  const [expanded, setIsExpanded] = useLocalStorage<boolean>({
    /**
     * Only used for internal toggle state. Not using as a CONST to avoid confusion.
     */
    key: 'weave-ephemeral-expanded',
    defaultValue: true,
  });

  const boolValue = !!rows && rows.length > 0;

  useEffect(() => {
    // get ref div height
    const newHeight = ref.current?.getBoundingClientRect().height ?? height;
    if (ref.current) {
      setHeight(newHeight);
    }

    onChangeVisibility({ isVisible: boolValue, height: newHeight });
  }, [boolValue, rows?.length, expanded]);

  if (!boolValue) {
    return null;
  }
  return (
    <div
      className='ephemeral-warning-banner'
      ref={ref}
      onClick={() => setIsExpanded(!expanded)}
      css={{ cursor: 'pointer' }}
    >
      <BannerNotification
        {...props}
        title={title ?? t('Ephemeral Warning')}
        status={status ?? 'info'}
        css={{ width: '100%', maxWidth: 'unset' }}
      >
        <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2), justifyContent: 'space-between' }}>
          <div>
            <b>{t('Ephemeral branches active: {{count}}', { count: rows.length })}</b>
          </div>
          <div>
            <div>
              <Icon height={16} name={expanded ? 'caret-right' : 'caret-down'} />
            </div>
          </div>
        </div>
        {expanded && (
          <ul>
            {rows.map(({ id, name, value }) => (
              <li key={id}>
                {name}: {value}
              </li>
            ))}
          </ul>
        )}
      </BannerNotification>
    </div>
  );
};
